import {
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ApiService } from '../../lib/api.service';
import { StorageService } from '../../lib/storage.service';
import { LogService } from '../../lib/log.service';
import { Router } from '@angular/router';
import { User } from '../../models/user';
import { CounselorUser } from '../../models/counselor-user';
import { Organization } from '../../models/organization';
import { UserService } from '../../lib/user.service';
import { ModalService } from '../../lib/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { of, Subject, Subscription } from 'rxjs';
import * as _ from 'lodash';
import { CounselorUserAssociations } from '../../components/admin/counselor-user-associations/counselor-user-associations';
import { CreateAssociation } from '../../components/admin/create-association/create-association';
@Component({
    selector: 'user-listing-admin.component',
    templateUrl: 'user-listing-admin.component.html',
    styleUrls: ['./user-listing-admin.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class UserListingAdminPage implements OnInit, OnDestroy {
    /**
     * Column filters push into these instead of calling the API directly, so
     * typing fires one request rather than one per keystroke, and switchMap
     * discards superseded responses that would otherwise land out of order.
     */
    private loadMore$ = new Subject<void>();
    private listInternalUsers$ = new Subject<void>();
    private subscriptions: Subscription[] = [];

    @ViewChild(CounselorUserAssociations)
    associationsList: CounselorUserAssociations;

    user: User;
    users: User[] = [];
    pendingUsers: CounselorUser[] = [];
    activeusers: User[] = [];
    popup: any;
    syncPopup: any;
    isLoaded = false;
    cols: Array<string> = ['', '', '', '', ''];
    internalCols: Array<string> = ['', '', '', '', ''];
    userShare: boolean = false;
    userListing: boolean = false;
    inviteSubscribe: boolean = false;
    internalUserOffset: number = 0;
    externalUserOffset: number = 0;
    offset: number = 0;
    invites: any;
    statuses: any;
    organization: Organization;

    externalUserRoles: Array<string> = [];
    internalUserRoles: Array<string> = [];
    userStatuses: Array<string> = [];
    externalUsersLoading: boolean = true;
    internalUsersLoading: boolean = true;
    internalUsers: any = [];
    organizations: any = [];
    seletedTab: number = 0;
    isInternal: boolean;
    isAdmin: boolean;

    // Pagination
    externalCurrentPage: number = 1;
    externalPageSize: number = 50;
    externalTotalItems: number = 0;
    internalCurrentPage: number = 1;
    internalPageSize: number = 50;
    internalTotalItems: number = 0;

    constructor(
        private api: ApiService,
        private storage: StorageService,
        private router: Router,
        private modalService: ModalService,
        private log: LogService,
        private translate: TranslateService,
        private userService: UserService
    ) {
        this.user = this.userService.getUser();
        /*
         * For now,  all internal users will have access to user listing
         */
        this.isInternal = this.userService.isInternal(this.user);
        this.isAdmin = this.userService.isAdmin(this.user);

        this.externalUserRoles = ['user', 'professional', 'orgadmin'];
        this.internalUserRoles = ['admin', 'superadmin', 'relationshipmanager'];
        this.userStatuses = ['approved', 'unconfirmed', 'disabled'];
        this.subscriptions.push(
            this.loadMore$
                .pipe(
                    debounceTime(400),
                    switchMap(() => {
                        this.externalUsersLoading = true;
                        return this.api
                            .get(
                                'admin/externalusers',
                                this.externalFilterParams()
                            )
                            .pipe(
                                catchError((error: any) =>
                                    this.onListingError(error)
                                )
                            );
                    })
                )
                .subscribe((results: any) => {
                    if (results) {
                        this.setExternalUsers(results.data);
                    }
                    this.isLoaded = true;
                    this.externalUsersLoading = false;
                })
        );

        this.subscriptions.push(
            this.listInternalUsers$
                .pipe(
                    debounceTime(400),
                    switchMap(() => {
                        this.internalUsersLoading = true;
                        return this.api
                            .get(
                                'users/internalusers',
                                this.internalFilterParams()
                            )
                            .pipe(
                                catchError((error: any) =>
                                    this.onListingError(error)
                                )
                            );
                    })
                )
                .subscribe((results: any) => {
                    if (results) {
                        this.setInternalUsers(results.data);
                    }
                    this.isLoaded = true;
                    this.internalUsersLoading = false;
                })
        );
    }

    /** Keeps a failed request from tearing down the debounced filter stream. */
    private onListingError(error: any) {
        this.log.error('Error loading. ' + error.message);
        return of(null);
    }

    ngOnInit() {
        this.translate.stream('userListing.popup').subscribe((res: any) => {
            this.popup = res;
        });

        this.translate.stream('userListing.statuses').subscribe((res: any) => {
            this.statuses = res;
        });

        this.translate.stream('sync.popup').subscribe((res: any) => {
            this.syncPopup = res;
        });

        this.api.get('admin/organizations').subscribe(
            (results: any) => {
                this.organizations = Organization.initializeArray(results.data);
            },
            (error: any) => {
                this.log.error('Error loading. ' + error.message);
            }
        );

        this.loadExternalUsers();
        this.listInternalUsers();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.activeusers = [];
        this.internalUsers = [];
        this.externalUserOffset = 0;
        this.internalUserOffset = 0;
    }

    valueChange(i) {
        this.externalUserOffset = 0;
        this.externalCurrentPage = 1;
        this.loadMore$.next();
    }

    reset() {
        let valid = true;

        for (let i = 0; i < this.cols.length; i++) {
            if (this.cols[i] !== '') {
                valid = false;
            }
        }
        return valid;
    }

    private externalFilterParams() {
        return {
            Limit: 5000,
            Offset: 0,
            Name: this.cols[0],
            Email: this.cols[1],
            OrganizationName: this.cols[2],
            UserType: this.cols[3] || '',
            Status: this.cols[4] || '',
        };
    }

    private setExternalUsers(rows: any[]) {
        const users = [];
        for (let user of rows) {
            users.push(new User(user));
        }
        this.activeusers = users;
        this.externalTotalItems = users.length;
    }

    loadExternalUsers() {
        this.externalUsersLoading = true;

        this.api
            .get('admin/externalusers', this.externalFilterParams())
            .subscribe(
                (results: any) => {
                    this.setExternalUsers(results.data);
                    this.isLoaded = true;
                    this.externalUsersLoading = false;
                },
                (error: any) => {
                    this.log.error('Error loading. ' + error.message);
                    this.isLoaded = true;
                    this.externalUsersLoading = false;
                }
            );
    }

    onExternalPageChange(page: number) {
        this.externalCurrentPage = page;
    }

    onInternalPageChange(page: number) {
        this.internalCurrentPage = page;
    }

    createUser(type?: String) {
        this.router.navigate(['/admin/user/create']);
    }

    createAssociation() {
        this.modalService
            .showComponent(CreateAssociation, null, '', false)
            .afterClosed()
            .subscribe((created: boolean) => {
                if (created) {
                    this.modalService.showAlert(
                        'Success',
                        'The user and counselor were associated.'
                    );
                    if (this.associationsList) {
                        this.associationsList.loadListing();
                    }
                }
            });
    }

    activateUser(userID: number, status: number) {
        const activate = status ? 'approved' : 'disabled';
        this.api
            .post(
                'users/activate',
                { UserID: userID, Status: activate },
                true,
                false
            )
            .subscribe(
                (data: any) => {
                    const index = this.activeusers.findIndex(
                        user => user.id === userID
                    );
                    this.activeusers[index].status = status
                        ? 'approved'
                        : 'disabled';
                },
                (error: any) => {
                    this.modalService.showAlert(
                        'Error',
                        'Something went wrong. ' + error.message
                    );
                }
            );
    }

    deleteUser(userID: number) {
        this.modalService
            .showConfirmation(
                'Delete',
                'Are you sure you want to delete this account?'
            )
            .afterClosed()
            .subscribe(result => {
                if (result) {
                    this.api.delete('users/' + userID).subscribe(
                        (result: any) => {
                            this.modalService.showAlert(
                                'Success',
                                'User has been deleted'
                            );
                            this.loadExternalUsers();
                        },
                        (error: any) => {
                            this.modalService.showAlert(
                                'Error',
                                'Something went wrong. Please try again.'
                            );
                        }
                    );
                }
            });
    }

    /**
     * List internal users
     */
    private internalFilterParams() {
        return {
            Limit: 5000,
            Offset: 0,
            Name: this.internalCols[0],
            Email: this.internalCols[1],
            OrganizationName: this.internalCols[2],
            UserType: this.internalCols[3] || '',
            Status: this.internalCols[4] || '',
        };
    }

    private setInternalUsers(rows: any[]) {
        const users = [];
        for (let user of rows) {
            users.push(new User(user));
        }
        this.internalUsers = users;
        this.internalTotalItems = users.length;
    }

    listInternalUsers() {
        this.internalUsersLoading = true;
        this.api
            .get('users/internalusers', this.internalFilterParams())
            .subscribe(
                (results: any) => {
                    this.setInternalUsers(results.data);
                    this.isLoaded = true;
                    this.internalUsersLoading = false;
                },
                (error: any) => {
                    this.log.error('Error loading. ' + error.message);
                    this.isLoaded = true;
                    this.internalUsersLoading = false;
                }
            );
    }

    /**
     * Internal user filter change
     */
    internalUservalueChange(i) {
        this.internalUserOffset = 0;
        this.internalCurrentPage = 1;
        this.listInternalUsers$.next();
    }

    showInvite(inviteType: string, userId?: any) {
        if (userId) {
            this.router.navigate(['/admin/user/invite', userId]);
        } else {
            this.router.navigate(['/admin/user/invite']);
        }
    }

    onEditInternalUser(selectedUserId, userType) {
        this.router.navigate(['/admin/user', selectedUserId, 'edit']);
    }
}
